import { fbt } from 'fbtee'
import { ExternalLink, Martini } from 'lucide-react'
import { pregameWhenLabel } from '../lib/pregameLabels'
import { pregameGoingIds } from '@shared/pregames'
import type { Pregame } from '../types'

/**
 * A hero card's footer when the viewer hosts or is invited to a pre-game for
 * it: the first one's time, place and how many are going, plus how many more pre-games there are.
 * With an address it opens Maps; without one, a tap falls through to the card.
 */
export function PregameStrip({ pregames, locale }: { pregames: Pregame[]; locale: string }) {
  const [first, ...rest] = pregames
  if (!first) return null
  const when = pregameWhenLabel(first, locale)
  const place = first.placeName || first.address
  const goingCount = pregameGoingIds(first).length
  const going = goingCount > 1
    ? fbt(fbt.plural('going', goingCount, { showCount: 'yes', many: 'going' }), 'How many people are going to the pre-game shown on a Home hero card')
    : null

  const content = (
    <>
      <Martini className="size-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold">
          {fbt('Pre-game', 'Label at the bottom of a Home hero card for the pre-game before that event')}
        </span>
        {[when, place, going].filter(Boolean).map((part, i) => (
          <span key={i} className="text-on-image/80"> · {part}</span>
        ))}
      </span>
      {rest.length > 0 && <span className="shrink-0 text-xs text-on-image/70">+{rest.length}</span>}
      {first.address && <ExternalLink className="size-3.5 shrink-0 text-on-image/70" />}
    </>
  )
  const className = 'flex items-center gap-2 border-t border-on-image/15 pt-2.5 text-left text-sm text-on-image'

  return first.address ? (
    <a
      href={`https://maps.google.com/?q=${encodeURIComponent(first.address)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hero-pregame"
      // The card opens the event; this goes to the pre-game's place instead.
      onClick={e => e.stopPropagation()}
      className={className}
    >
      {content}
    </a>
  ) : (
    <div data-testid="hero-pregame" className={className}>{content}</div>
  )
}

/** "🍸 9 PM" appended to an agenda row's meta line when its event has a pre-game. */
export function PregameMark({ pregame, locale }: { pregame: Pregame; locale: string }) {
  const when = pregameWhenLabel(pregame, locale)
  return (
    <span className="inline-flex shrink-0 items-center gap-1" data-testid="pregame-mark">
      <Martini className="size-3.5" aria-label={fbt('Pre-game', 'Accessible label of the martini icon marking an event that has a pre-game')} />
      {when}
    </span>
  )
}

/** Marks a person in a people list as going to a pre-game the viewer can see. */
export function PregameGoerIcon() {
  return (
    <Martini
      className="size-4 shrink-0 text-primary"
      aria-label={fbt('Going to the pre-game', "Icon on a person's row in a people list marking that they're going to a pre-game")}
      data-testid="people-pregame-mark"
    />
  )
}
